/* eslint-disable no-magic-numbers */
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { useLocation, useNavigate } from 'react-router-dom';

import { useSnackbar } from 'notistack';

import Route from 'routes/Route';

import { useModal } from 'hooks/useModal';

import Pagination from 'components/Pagination';

import pageStyles, { LIST_ITEM_TEXT_PRIMARY } from './styles';
import EvolutionModal from './EvolutionModal';

const PAGE_SIZE = 5;

interface EvolutionInterface {
  totalCount: number;
  evolutions: {
    id: number;
    name: string;
    text: string;
    created_at: string;
    pictures: string[];
  }[];
}

interface EvolutionForm {
  created_at: string;
  name: string;
  text: string;
  id: number;
  pictures: string[];
}

type EvolutionLocationState = { id?: number } | null | undefined;

const Evolution = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const location = useLocation();
  const navigate = useNavigate();
  const pacientId = (location.state as EvolutionLocationState)?.id;
  const [initialDate, setInitialDate] = useState('');
  const [search, setSearch] = useState('');
  const [finalDate, setFinalDate] = useState('');
  const { isOpen, handleClose, handleOpen } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (pacientId != null) {
      return;
    }
    enqueueSnackbar('Abra as evoluções pelo menu do paciente.', {
      variant: 'warning',
    });
    navigate(Route.DASHBOARD, { replace: true });
  }, [enqueueSnackbar, navigate, pacientId]);

  const [rows, setRows] = useState<EvolutionInterface | undefined>();
  const [evolutionFormState, setEvolutionFormState] = useState<
    EvolutionForm | undefined
  >();
  const [listLoading, setListLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const setGroupFormStateHandler = useCallback(
    (newFormState: EvolutionForm | undefined) => {
      setEvolutionFormState(newFormState);
      handleOpen();
    },
    [handleOpen],
  );

  const getFilter = useCallback(() => {
    let filter: { filter: Record<string, unknown> } = { filter: {} };
    if (initialDate) {
      filter = {
        filter: {
          from: new Date(initialDate),
        },
      };
    }
    if (finalDate) {
      filter = {
        filter: {
          ...filter.filter,
          until: new Date(finalDate),
        },
      };
    }

    if (search) {
      filter = {
        filter: {
          ...filter.filter,
          textFilter: search,
        },
      };
    }
    return filter;
  }, [finalDate, initialDate, search]);

  const handlePagination = useCallback(
    (value: number) => {
      if (!rows?.evolutions) return;
      if (value === page) return;

      const { evolutions } = rows;
      let cursor;
      let backwards = false;
      if (value > page) {
        cursor = evolutions[evolutions.length - 1]?.id ?? 0;
      } else {
        cursor = evolutions[0]?.id ?? 0;
        backwards = true;
      }

      const filter = getFilter();
      setPageLoading(true);
      axios
        .get<EvolutionInterface>('get-evolution', {
          params: {
            ...filter,
            pacientId,
            cursor,
            backwards,
          },
        })
        .then(response => {
          setRows(response.data);
          setPage(value);
        })
        .catch(() =>
          enqueueSnackbar('Ocorreu um erro ao resgatar evolucao', {
            variant: 'error',
          }),
        )
        .finally(() => setPageLoading(false));
    },
    [enqueueSnackbar, getFilter, page, pacientId, rows],
  );

  useEffect(() => {
    if (pacientId == null) {
      return;
    }
    setListLoading(true);
    axios
      .get<EvolutionInterface>('get-evolution', {
        params: {
          pacientId,
        },
      })
      .then(response => {
        setRows(response.data);
        setPage(1);
      })
      .catch(() =>
        enqueueSnackbar('Ocorreu um erro ao resgatar evolucao', {
          variant: 'error',
        }),
      )
      .finally(() => setListLoading(false));
  }, [enqueueSnackbar, pacientId]);

  const handleFilter = useCallback(() => {
    if (pacientId == null) {
      return;
    }
    setFilterLoading(true);
    const filter = getFilter();
    axios
      .get<EvolutionInterface>('get-evolution', {
        params: {
          pacientId,
          ...filter,
        },
      })
      .then(response => {
        setRows(response.data);
        setPage(1);
      })
      .catch(() =>
        enqueueSnackbar('Ocorreu um erro ao resgatar evolucao', {
          variant: 'error',
        }),
      )
      .finally(() => setFilterLoading(false));
  }, [enqueueSnackbar, getFilter, pacientId]);

  const rowData = useMemo(() => {
    if (listLoading || filterLoading || pageLoading) {
      return (
        <Box sx={pageStyles.loadingBox}>
          <CircularProgress aria-label="Carregando evoluções" />
        </Box>
      );
    }
    if (!rows?.evolutions?.length) {
      return (
        <Box sx={pageStyles.emptyState}>
          <InboxOutlinedIcon sx={pageStyles.emptyIcon} />
          <Typography variant="body1" sx={pageStyles.emptyText}>
            Nenhuma evolução encontrada
          </Typography>
        </Box>
      );
    }
    return rows.evolutions.map(row => (
      <Paper key={row.id} variant="outlined" sx={pageStyles.evolutionRowPaper}>
        <ListItemButton
          onClick={(): void => setGroupFormStateHandler(row)}
          alignItems="flex-start"
          sx={pageStyles.listItemButton}
        >
          <ListItemText
            primary={row.name}
            secondary={new Date(row.created_at).toLocaleDateString()}
            slotProps={{ primary: LIST_ITEM_TEXT_PRIMARY }}
          />
        </ListItemButton>
      </Paper>
    ));
  }, [filterLoading, listLoading, pageLoading, rows, setGroupFormStateHandler]);

  const handleInitialDate = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setInitialDate(event.target.value);
    },
    [],
  );

  const handleFinalDate = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setFinalDate(event.target.value);
    },
    [],
  );

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearch(event.target.value);
    },
    [],
  );

  const busy = listLoading || filterLoading || pageLoading;

  if (pacientId == null) {
    return (
      <Container maxWidth="md" sx={pageStyles.pageContainer}>
        <Box sx={pageStyles.loadingBox}>
          <CircularProgress aria-label="Redirecionando" />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={pageStyles.pageContainer}>
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={pageStyles.pageTitle}
      >
        Evoluções
      </Typography>

      <Paper variant="outlined" sx={pageStyles.filtersPaper}>
        <Stack spacing={2}>
          <TextField
            name="search"
            label="Buscar no texto"
            value={search}
            onChange={handleSearch}
            fullWidth
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" aria-hidden />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={pageStyles.dateRowStack}
          >
            <TextField
              type="date"
              name="initialDate"
              label="De"
              value={initialDate}
              onChange={handleInitialDate}
              fullWidth
              size="small"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthOutlinedIcon
                        color="action"
                        fontSize="small"
                        aria-hidden
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              type="date"
              name="finalDate"
              label="Até"
              value={finalDate}
              onChange={handleFinalDate}
              fullWidth
              size="small"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthOutlinedIcon
                        color="action"
                        fontSize="small"
                        aria-hidden
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleFilter}
              disabled={busy}
              sx={pageStyles.filterButton}
            >
              {filterLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Filtrar'
              )}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={pageStyles.listPaper} aria-busy={busy}>
        <List sx={pageStyles.container} disablePadding>
          {rowData}
        </List>
      </Paper>

      <EvolutionModal
        isOpen={isOpen}
        handleClose={handleClose}
        data={evolutionFormState}
      />
      {rows && rows.totalCount > 0 ? (
        <Box sx={pageStyles.paginationWrap}>
          <Pagination
            total={Math.ceil(rows.totalCount / PAGE_SIZE)}
            page={page}
            onChange={handlePagination}
            disabled={busy}
          />
        </Box>
      ) : null}
    </Container>
  );
};

export default Evolution;
